import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { CardField } from "@/components/RecordCard";
import {
  getInventoryValuationReport,
  getTrialBalance,
  type InventoryValuationReport,
  listChartAccounts,
  listPayrollJournals,
  type PayrollJournalReview,
  type PayrollJournalReviewEntry,
  type PayrollJournalTaxLine,
  postPayrollJournals,
  postProcurementGrniJournals,
} from "@/lib/api";
import {
  reviewInventoryBooks,
  reviewPayrollAmounts,
  type InventoryBooksReview,
} from "@/lib/source-journal-review";

const DISPLAY_CURRENCY = "USD";

function toErrorMessage(cause: unknown) {
  return cause instanceof Error ? cause.message : String(cause);
}

function money(cents: number) {
  return formatMoney({ cents, currency: DISPLAY_CURRENCY });
}

function taxSummary(lines: PayrollJournalTaxLine[]) {
  if (lines.length === 0) return null;
  return lines
    .map((line) => `${line.tax_kind} ${money(line.amount_cents)}`)
    .join(", ");
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function booksSentence(review: InventoryBooksReview) {
  if (review.problem === "no_account") {
    return "These books have no inventory account. The stock value is not on the balance sheet.";
  }
  if (review.problem === "unknown") {
    return "The inventory accounts are on the chart, but their trial-balance amounts could not be read.";
  }
  if (review.problem === "overflow") {
    return "These amounts are too large to compare.";
  }
  const book = money(review.book_balance_cents ?? 0);
  return `Inventory on the books is ${book}. ${methodSentence(
    "average-cost",
    review.average_difference_cents ?? 0,
  )} ${methodSentence("FIFO", review.fifo_difference_cents ?? 0)}`;
}

function methodSentence(method: string, difference: number) {
  if (difference === 0) return `The books match the ${method} value.`;
  const gap = money(Math.abs(difference));
  const direction = difference > 0 ? "higher" : "lower";
  return `The books are ${gap} ${direction} than the ${method} value.`;
}

export function SourceJournalPosting() {
  return (
    <div className="grid gap-6">
      <PageHeader title="Source journal posting" />
      <InventoryValuationReview />
      <PayrollJournalReviewCard />
      <GrniPosting />
    </div>
  );
}

function InventoryValuationReview() {
  const [report, setReport] = useState<InventoryValuationReport | null>(null);
  const [books, setBooks] = useState<InventoryBooksReview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [booksError, setBooksError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [booksLoading, setBooksLoading] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getInventoryValuationReport()
      .then((next) => {
        if (!cancelled) setReport(next);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(toErrorMessage(cause));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reload]);

  useEffect(() => {
    if (!report) return;
    let cancelled = false;
    setBooksLoading(true);
    setBooksError(null);
    setBooks(null);
    const snapshot = report;
    Promise.all([listChartAccounts(), getTrialBalance({ as_at: todayIso() })])
      .then(([accounts, trial]) => {
        if (cancelled) return;
        const rows = trial.sections.flatMap((section) => section.rows);
        setBooks(
          reviewInventoryBooks({
            accounts,
            rows,
            total_average_value_cents: snapshot.total_average_value_cents,
            total_fifo_value_cents: snapshot.total_fifo_value_cents,
            unvalued_count: snapshot.skus.filter((sku) => !sku.valued).length,
          }),
        );
      })
      .catch((cause: unknown) => {
        if (!cancelled) setBooksError(toErrorMessage(cause));
      })
      .finally(() => {
        if (!cancelled) setBooksLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [report, reload]);

  return (
    <section className="surface grid gap-3 rounded-2xl p-5">
      <h2 className="text-lg font-semibold tracking-tight">Inventory valuation</h2>
      <p className="text-sm text-[var(--muted-foreground)]">
        Operations owns the quantities and the cost. This check compares that
        value with the inventory accounts on the trial balance. It does not
        post an adjustment. A difference is cleared by a journal you enter, or
        by the stock movements in Operations.
      </p>
      {booksError ? (
        <div
          role="alert"
          className="rounded-xl border border-[var(--danger)] p-3 text-sm text-[var(--danger)]"
        >
          <p>Couldn’t check the inventory accounts: {booksError}</p>
          <div className="mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setReload((current) => current + 1)}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}
      {booksLoading ? (
        <p className="text-sm text-[var(--muted-foreground)]">
          Checking the inventory accounts…
        </p>
      ) : null}
      {books && !booksError ? (
        <div className="grid gap-2 text-sm" data-testid="inventory-books-check">
          <p>{booksSentence(books)}</p>
          {books.unvalued_count > 0 ? (
            <p className="text-[var(--muted-foreground)]">
              {books.unvalued_count === 1
                ? "1 item is left out of both values."
                : `${books.unvalued_count} items are left out of both values.`}
            </p>
          ) : null}
          {books.accounts.length > 0 ? (
            <ul className="grid gap-1">
              {books.accounts.map((account) => (
                <li
                  key={account.id}
                  className="flex flex-wrap items-baseline justify-between gap-2"
                >
                  <Link
                    to="/chart-of-accounts/$accountId"
                    params={{ accountId: account.id }}
                    className="underline-offset-4 hover:underline"
                  >
                    <span className="mr-2 font-mono text-xs">{account.code}</span>
                    {account.name}
                  </Link>
                  <span className="tabular-nums">{money(account.balance_cents)}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {error ? (
        <div
          role="alert"
          className="rounded-xl border border-[var(--danger)] p-3 text-sm text-[var(--danger)]"
        >
          <p>Couldn’t load the inventory valuation: {error}</p>
          <div className="mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setReload((current) => current + 1)}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}
      {loading ? (
        <p className="text-sm text-[var(--muted-foreground)]">
          Loading inventory valuation…
        </p>
      ) : null}
      {!loading && !error && report && report.skus.length === 0 ? (
        <p className="text-sm text-[var(--muted-foreground)]">
          Operations has no inventory items to value.
        </p>
      ) : null}
      {report && report.skus.length > 0 ? (
        <>
          <div
            className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block"
            data-testid="inventory-valuation-table"
          >
            <div className="table-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>SKU</TableHead>
                    <TableHead className="text-right">On hand</TableHead>
                    <TableHead className="text-right">Avg-cost value</TableHead>
                    <TableHead className="text-right">FIFO value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.skus.map((sku) => (
                    <TableRow key={sku.sku_id}>
                      <TableCell>
                        <span className="mr-2 font-mono text-xs text-[var(--muted-foreground)]">
                          {sku.code}
                        </span>
                        {sku.name}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {sku.on_hand_units}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {sku.valued && sku.average_value_cents !== null
                          ? money(sku.average_value_cents)
                          : "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {sku.valued && sku.fifo_value_cents !== null
                          ? money(sku.fifo_value_cents)
                          : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell className="font-semibold">Total</TableCell>
                    <TableCell />
                    <TableCell className="text-right font-semibold tabular-nums">
                      {money(report.total_average_value_cents)}
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">
                      {money(report.total_fifo_value_cents)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </div>

          <div
            className="stack-list grid gap-3 min-[64rem]:hidden"
            data-testid="inventory-valuation-stack"
          >
            {report.skus.map((sku) => (
              <article
                key={sku.sku_id}
                className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
              >
                <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                  <span className="record-title font-medium">{sku.name}</span>
                  <span className="font-mono text-xs text-[var(--muted-foreground)]">
                    {sku.code}
                  </span>
                </div>
                <CardField label="On hand">
                  <span className="tabular-nums">{sku.on_hand_units}</span>
                </CardField>
                <CardField label="Avg-cost value">
                  <span className="tabular-nums">
                    {sku.valued && sku.average_value_cents !== null
                      ? money(sku.average_value_cents)
                      : "—"}
                  </span>
                </CardField>
                <CardField label="FIFO value">
                  <span className="tabular-nums">
                    {sku.valued && sku.fifo_value_cents !== null
                      ? money(sku.fifo_value_cents)
                      : "—"}
                  </span>
                </CardField>
              </article>
            ))}
            <article className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4">
              <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                <span className="font-semibold">Total</span>
              </div>
              <CardField label="Avg-cost value">
                <span className="tabular-nums">
                  {money(report.total_average_value_cents)}
                </span>
              </CardField>
              <CardField label="FIFO value">
                <span className="tabular-nums">
                  {money(report.total_fifo_value_cents)}
                </span>
              </CardField>
            </article>
          </div>
        </>
      ) : null}
    </section>
  );
}

function missingAccountSentence(role: string | undefined) {
  switch (role) {
    case "wages":
      return "These books have no wages account for this journal.";
    case "employer_tax":
      return "These books have no employer tax account for this journal.";
    case "income_tax":
      return "These books have no income tax account for this journal.";
    case "payroll_tax":
      return "These books have no payroll tax account for this journal.";
    case "net_pay":
      return "These books have no wages payable account for this journal.";
    default:
      return "These books have no account for this journal.";
  }
}

type PayrollRowState =
  | { kind: "posted" }
  | { kind: "note"; reason: string }
  | { kind: "block"; reason: string }
  | { kind: "ready" };

function payrollRowState(entry: PayrollJournalReviewEntry): PayrollRowState {
  if (entry.journal_entry_id) return { kind: "posted" };
  const amounts = reviewPayrollAmounts(entry);
  if (amounts.status === "skip") {
    return {
      kind: "note",
      reason: amounts.reason ?? "This row stays off the ledger.",
    };
  }
  if (amounts.status === "refuse") {
    return {
      kind: "block",
      reason: amounts.reason ?? "This row cannot become a journal.",
    };
  }
  const posting = entry.posting;
  if (posting?.status === "ready" && posting.lines.length > 0) {
    return { kind: "ready" };
  }
  if (posting?.status === "missing_account") {
    return {
      kind: "block",
      reason: missingAccountSentence(posting.missing_account),
    };
  }
  return {
    kind: "block",
    reason: "These books have no account for this journal.",
  };
}

function PayrollLedgerCell({ entry }: { entry: PayrollJournalReviewEntry }) {
  const state = payrollRowState(entry);
  if (state.kind === "posted" && entry.journal_entry_id) {
    return (
      <Link
        to="/journal/$entryId"
        params={{ entryId: entry.journal_entry_id }}
        className="underline-offset-4 hover:underline"
      >
        View journal
      </Link>
    );
  }
  if (state.kind === "note" || state.kind === "block") {
    return <span>{state.reason}</span>;
  }
  const lines = entry.posting?.lines ?? [];
  return (
    <span>
      <span className="block">Waiting</span>
      <span className="block text-xs text-[var(--muted-foreground)]">
        {lines.map((line, index) => {
          const debit = line.debit_cents > 0;
          return (
            <span key={`${line.account_id}-${index}`}>
              {index > 0 ? ", " : null}
              {debit ? "Debit" : "Credit"}{" "}
              <Link
                to="/chart-of-accounts/$accountId"
                params={{ accountId: line.account_id }}
                className="underline-offset-4 hover:underline"
              >
                {line.code} {line.name}
              </Link>{" "}
              <span className="tabular-nums">
                {money(debit ? line.debit_cents : line.credit_cents)}
              </span>
            </span>
          );
        })}
      </span>
    </span>
  );
}

function PayrollJournalReviewCard() {
  const [review, setReview] = useState<PayrollJournalReview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listPayrollJournals()
      .then((next) => {
        if (!cancelled) setReview(next);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(toErrorMessage(cause));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const postJournals = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await postPayrollJournals();
      setNotice(
        result.posted_count === 0
          ? "Nothing to post — no payroll journals are waiting for the ledger."
          : `Posted ${result.posted_count} payroll ${
              result.posted_count === 1 ? "journal" : "journals"
            } to the ledger.`,
      );
      setReload((current) => current + 1);
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  const decisions =
    review?.entries.map((entry) => ({
      entry,
      state: payrollRowState(entry),
    })) ?? [];
  const blocking = decisions.filter((row) => row.state.kind === "block");
  const postable = decisions.filter((row) => row.state.kind === "ready");
  const waiting = decisions.filter((row) => row.entry.journal_entry_id === null).length;

  const entryTaxes = (entry: PayrollJournalReview["entries"][number]) =>
    [
      taxSummary(entry.tax_lines),
      taxSummary(entry.employer_tax_lines),
    ]
      .filter(Boolean)
      .join("; ");

  return (
    <section className="surface grid gap-3 rounded-2xl p-5">
      <h2 className="text-lg font-semibold tracking-tight">Payroll journals</h2>
      <p className="text-sm text-[var(--muted-foreground)]">
        HR prepares payroll. This review reads those journals and can post the
        waiting ones to the ledger. Each waiting row names the chart accounts
        the ledger will debit and credit. Accounting does not create employees
        or start a payroll run.
      </p>
      {error ? (
        <p role="alert" className="text-sm text-[var(--danger)]">
          {error}
        </p>
      ) : null}
      {notice ? (
        <p className="text-sm text-[var(--muted-foreground)]">{notice}</p>
      ) : null}
      {loading && !review ? (
        <p className="text-sm text-[var(--muted-foreground)]">
          Loading payroll journals…
        </p>
      ) : null}
      {review && review.entries.length === 0 ? (
        <p className="text-sm text-[var(--muted-foreground)]">
          No payroll journals are waiting.
        </p>
      ) : null}
      {review && review.entries.length > 0 ? (
        <>
          <div
            className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block"
            data-testid="payroll-journal-table"
          >
            <div className="table-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">Tax withheld</TableHead>
                    <TableHead className="text-right">Net</TableHead>
                    <TableHead className="text-right">Employer tax</TableHead>
                    <TableHead>Ledger</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {review.entries.map((entry) => {
                    const employee =
                      entry.employee_name ||
                      entry.employee_number ||
                      entry.profile_id;
                    const taxes = entryTaxes(entry);
                    return (
                      <TableRow key={entry.id}>
                        <TableCell>
                          <div>{employee}</div>
                          {entry.employee_number ? (
                            <div className="font-mono text-xs text-[var(--muted-foreground)]">
                              {entry.employee_number}
                            </div>
                          ) : null}
                          {taxes ? (
                            <div className="text-xs text-[var(--muted-foreground)]">
                              {taxes}
                            </div>
                          ) : null}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {entry.period_start} – {entry.period_end}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {money(entry.gross_cents)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {money(entry.tax_cents)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {money(entry.net_cents)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {money(entry.employer_tax_cents)}
                        </TableCell>
                        <TableCell>
                          <PayrollLedgerCell entry={entry} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>

          <div
            className="stack-list grid gap-3 min-[64rem]:hidden"
            data-testid="payroll-journal-stack"
          >
            {review.entries.map((entry) => {
              const employee =
                entry.employee_name ||
                entry.employee_number ||
                entry.profile_id;
              const taxes = entryTaxes(entry);
              return (
                <article
                  key={entry.id}
                  className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                >
                  <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                    <span className="record-title font-medium">{employee}</span>
                    <span className="tabular-nums">
                      {money(entry.net_cents)}
                    </span>
                  </div>
                  {entry.employee_number ? (
                    <CardField label="Employee number">
                      <span className="break-all font-mono text-xs">
                        {entry.employee_number}
                      </span>
                    </CardField>
                  ) : null}
                  <CardField label="Period">
                    {entry.period_start} – {entry.period_end}
                  </CardField>
                  <CardField label="Gross">
                    <span className="tabular-nums">
                      {money(entry.gross_cents)}
                    </span>
                  </CardField>
                  <CardField label="Tax withheld">
                    <span className="tabular-nums">
                      {money(entry.tax_cents)}
                    </span>
                  </CardField>
                  <CardField label="Employer tax">
                    <span className="tabular-nums">
                      {money(entry.employer_tax_cents)}
                    </span>
                  </CardField>
                  {taxes ? (
                    <CardField label="Tax lines">{taxes}</CardField>
                  ) : null}
                  <CardField label="Ledger">
                    <PayrollLedgerCell entry={entry} />
                  </CardField>
                </article>
              );
            })}
          </div>
        </>
      ) : null}
      {review && review.entries.length > 0 ? (
        <p className="text-sm text-[var(--muted-foreground)]">
          {waiting === 1
            ? "1 payroll journal is waiting for the ledger."
            : `${waiting} payroll journals are waiting for the ledger.`}
        </p>
      ) : null}
      {blocking.length > 0 ? (
        <p role="alert" className="text-sm text-[var(--danger)]">
          {blocking[0].entry.employee_name ||
            blocking[0].entry.employee_number ||
            "A payroll journal"}
          : {blocking[0].state.kind === "block" ? blocking[0].state.reason : ""}{" "}
          Posting stays off until every waiting row can become a journal.
        </p>
      ) : null}
      <Button
        type="button"
        className="w-fit"
        disabled={busy || postable.length === 0 || blocking.length > 0}
        onClick={() => setConfirmOpen(true)}
      >
        {busy ? "Posting…" : "Post payroll journals"}
      </Button>

      <Dialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!busy) setConfirmOpen(open);
        }}
        title="Post payroll journals?"
        description="Posts the waiting payroll journals to the ledger. This is bookkeeping — it does not start a payroll run and does not pay anyone."
      >
        <div className="grid gap-4">
          <p className="text-sm">
            {waiting === 1
              ? "1 payroll journal is waiting for the ledger."
              : `${waiting} payroll journals are waiting for the ledger.`}{" "}
            The journal entry is written to the books; paying people happens in
            HR and payroll, not here.
          </p>
          <div className="actions-row">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setConfirmOpen(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                setConfirmOpen(false);
                void postJournals();
              }}
              disabled={busy}
            >
              {busy ? "Posting…" : "Post payroll journals"}
            </Button>
          </div>
        </div>
      </Dialog>
    </section>
  );
}

function GrniPosting() {
  const [grniBusy, setGrniBusy] = useState(false);
  const [grniNotice, setGrniNotice] = useState<string | null>(null);
  const [grniError, setGrniError] = useState<string | null>(null);
  const [grniConfirmOpen, setGrniConfirmOpen] = useState(false);

  const postGrniJournals = async () => {
    setGrniBusy(true);
    setGrniError(null);
    setGrniNotice(null);
    try {
      const result = await postProcurementGrniJournals();
      setGrniNotice(
        result.posted_count === 0
          ? "Nothing to post — no goods-received-not-invoiced entries are waiting."
          : `Posted ${result.posted_count} goods-received-not-invoiced ${
              result.posted_count === 1 ? "journal" : "journals"
            } to the ledger.`,
      );
    } catch (cause) {
      setGrniError(toErrorMessage(cause));
    } finally {
      setGrniBusy(false);
    }
  };

  return (
    <section className="surface grid gap-2 rounded-2xl p-5">
      <h2 className="text-lg font-semibold tracking-tight">
        Goods received, not invoiced
      </h2>
      <p className="text-sm text-[var(--muted-foreground)]">
        Requisitions, purchase orders, and goods receipts are authored in
        operations, but posting the GRNI journal to the ledger is
        accounting's job.
      </p>
      {grniError ? (
        <p role="alert" className="text-sm text-[var(--danger)]">
          {grniError}
        </p>
      ) : null}
      {grniNotice ? (
        <p className="text-sm text-[var(--muted-foreground)]">{grniNotice}</p>
      ) : null}
      <Button
        type="button"
        className="w-fit"
        disabled={grniBusy}
        onClick={() => setGrniConfirmOpen(true)}
      >
        {grniBusy ? "Posting…" : "Post GRNI journals"}
      </Button>

      <Dialog
        open={grniConfirmOpen}
        onOpenChange={(open) => {
          if (!grniBusy) setGrniConfirmOpen(open);
        }}
        title="Post GRNI journals?"
        description="Posts all waiting goods-received-not-invoiced journals to the ledger. This is bookkeeping — it does not pay a supplier and does not send money."
      >
        <div className="grid gap-4">
          <p className="text-sm">
            The accrual is written to the books now. Paying the supplier
            happens later, from the bills and payments side.
          </p>
          <div className="actions-row">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setGrniConfirmOpen(false)}
              disabled={grniBusy}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                setGrniConfirmOpen(false);
                void postGrniJournals();
              }}
              disabled={grniBusy}
            >
              {grniBusy ? "Posting…" : "Post GRNI journals"}
            </Button>
          </div>
        </div>
      </Dialog>
    </section>
  );
}
